import React, { useState, useEffect, useMemo } from 'react';
import Breadcrumbs from '../../components/BreadCrumbs';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import Table from '../../components/tables/Table';
import { getPolygons, getPolygon, deletePolygon } from '../../services/dynacrop';
import { FaSearch, FaCopy, FaTimes, FaSatellite, FaCalendarAlt, FaMapMarkedAlt, FaLayerGroup, FaTrash, FaChevronDown, FaChevronUp, FaCloud, FaChartLine, FaExternalLinkAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { formatDate } from '../../utils/FormatDateTime';
import MapComponent from '../../components/MapComponent';
import { useNavigate } from 'react-router-dom';
import {
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';

/* -------------------- HELPERS -------------------- */

// Group observations by year for bar chart
const groupObservationsByYear = (observations) => {
    const grouped = {};
    observations?.forEach((date) => {
        const year = date.substring(0, 4);
        if (!grouped[year]) grouped[year] = 0;
        grouped[year]++;
    });
    return Object.entries(grouped).map(([year, count]) => ({ year, count }));
};

// Process cloud cover data for chart (last 12 months)
const processCloudCoverData = (cloudCover) => {
    if (!cloudCover) return [];
    const monthlyData = {};
    cloudCover.forEach(([date, cover]) => {
        const month = date.substring(0, 7);
        if (!monthlyData[month]) {
            monthlyData[month] = { sum: 0, count: 0 };
        }
        monthlyData[month].sum += cover * 100;
        monthlyData[month].count++;
    });
    return Object.entries(monthlyData)
        .map(([month, { sum, count }]) => ({
            month: month.substring(5), // Just MM
            cloudCover: Math.round(sum / count),
        }))
        .slice(-12);
};

// Calculate cloud statistics
const calculateCloudStats = (cloudCover) => {
    if (!cloudCover || cloudCover.length === 0) return { avg: 0, clear: 0, cloudy: 0 };
    const total = cloudCover.length;
    const clearDays = cloudCover.filter(([_, cover]) => cover <= 0.2).length;
    const cloudyDays = cloudCover.filter(([_, cover]) => cover > 0.5).length;
    const avgCover = cloudCover.reduce((sum, [_, cover]) => sum + cover, 0) / total;
    return {
        avg: Math.round(avgCover * 100),
        clear: Math.round((clearDays / total) * 100),
        cloudy: Math.round((cloudyDays / total) * 100),
    };
};

/* -------------------- CHART COMPONENTS -------------------- */

const MiniCloudChart = ({ data }) => {
    if (!data || data.length === 0) return <p className="text-xs text-gray-400">No cloud data</p>;
    return (
        <ResponsiveContainer width="100%" height={120}>
            <AreaChart data={data}>
                <defs>
                    <linearGradient id="cloudGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                </defs>
                <XAxis dataKey="month" tick={{ fontSize: 9 }} stroke="#9CA3AF" />
                <YAxis domain={[0, 100]} hide />
                <Tooltip
                    formatter={(val) => [`${val}%`, "Cloud"]}
                    contentStyle={{ fontSize: '11px', borderRadius: '6px' }}
                />
                <Area type="monotone" dataKey="cloudCover" stroke="#3B82F6" fill="url(#cloudGrad)" />
            </AreaChart>
        </ResponsiveContainer>
    );
};

const MiniObservationsChart = ({ data }) => {
    if (!data || data.length === 0) return <p className="text-xs text-gray-400">No observations</p>;
    const lastYears = data.slice(-6); // Last 6 years
    return (
        <ResponsiveContainer width="100%" height={120}>
            <BarChart data={lastYears}>
                <XAxis dataKey="year" tick={{ fontSize: 9 }} stroke="#9CA3AF" />
                <YAxis hide />
                <Tooltip
                    formatter={(val) => [val, "Obs"]}
                    contentStyle={{ fontSize: '11px', borderRadius: '6px' }}
                />
                <Bar dataKey="count" fill="#10B981" radius={[3, 3, 0, 0]} />
            </BarChart>
        </ResponsiveContainer>
    );
};

/* -------------------- SOURCE ACCORDION -------------------- */

const SourceAccordion = ({ source, isFirst = false }) => {
    const [open, setOpen] = useState(isFirst);

    const yearlyData = useMemo(() => groupObservationsByYear(source.valid_observations), [source.valid_observations]);
    const cloudCoverData = useMemo(() => processCloudCoverData(source.cloud_cover), [source.cloud_cover]);
    const cloudStats = useMemo(() => calculateCloudStats(source.cloud_cover), [source.cloud_cover]);
    const totalObs = source.valid_observations?.length || 0;
    const firstObs = source.valid_observations?.[0];
    const lastObs = source.valid_observations?.[totalObs - 1];

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'bg-green-100 text-green-700';
            case 'pending': return 'bg-yellow-100 text-yellow-700';
            case 'failed': return 'bg-red-100 text-red-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    // Get all indices from allowed_methods
    const allIndices = useMemo(() => {
        const indices = new Set();
        Object.values(source.allowed_methods || {}).forEach((arr) => {
            arr.forEach((idx) => indices.add(idx));
        });
        return Array.from(indices).sort();
    }, [source.allowed_methods]);

    return (
        <div className="border rounded-lg overflow-hidden bg-white">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
                <div className="flex items-center gap-2">
                    <FaLayerGroup className="text-gray-400" />
                    <div className="text-left">
                        <p className="font-semibold text-gray-800 text-sm">{source.name}</p>
                        <p className="text-xs text-gray-500">{totalObs.toLocaleString()} observations</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(source.status)}`}>
                        {source.status}
                    </span>
                    {open ? <FaChevronUp className="text-gray-400 text-xs" /> : <FaChevronDown className="text-gray-400 text-xs" />}
                </div>
            </button>

            {open && (
                <div className="p-3 border-t space-y-4">
                    {/* Date Range & Stats */}
                    <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-gray-50 rounded p-2">
                            <p className="text-lg font-bold text-gray-800">{totalObs}</p>
                            <p className="text-xs text-gray-500">Observations</p>
                        </div>
                        {source.cloud_cover && (
                            <>
                                <div className="bg-green-50 rounded p-2">
                                    <p className="text-lg font-bold text-green-600">{cloudStats.clear}%</p>
                                    <p className="text-xs text-green-600">Clear Days</p>
                                </div>
                                <div className="bg-blue-50 rounded p-2">
                                    <p className="text-lg font-bold text-blue-600">{cloudStats.avg}%</p>
                                    <p className="text-xs text-blue-600">Avg Cloud</p>
                                </div>
                            </>
                        )}
                        {!source.cloud_cover && (
                            <div className="bg-gray-50 rounded p-2 col-span-2">
                                <p className="text-xs text-gray-500">Date Range</p>
                                <p className="text-sm font-medium text-gray-700">{firstObs} → {lastObs}</p>
                            </div>
                        )}
                    </div>

                    {/* Cloud Cover Chart (if available) */}
                    {source.cloud_cover && cloudCoverData.length > 0 && (
                        <div>
                            <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                                <FaCloud className="text-blue-400" /> Monthly Cloud Cover (Last 12 Months)
                            </p>
                            <MiniCloudChart data={cloudCoverData} />
                        </div>
                    )}

                    {/* Observations by Year Chart */}
                    {yearlyData.length > 0 && (
                        <div>
                            <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                                <FaChartLine className="text-green-400" /> Observations by Year
                            </p>
                            <MiniObservationsChart data={yearlyData} />
                        </div>
                    )}

                    {/* Available Indices */}
                    {allIndices.length > 0 && (
                        <div>
                            <p className="text-xs text-gray-500 mb-2">Available Indices ({allIndices.length})</p>
                            <div className="flex flex-wrap gap-1">
                                {allIndices.map((idx) => (
                                    <span
                                        key={idx}
                                        className="px-2 py-1 bg-gradient-to-r from-green-50 to-blue-50 border border-green-100 rounded text-xs font-medium text-green-700"
                                    >
                                        {idx}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Methods */}
                    <div>
                        <p className="text-xs text-gray-500 mb-2">Analysis Methods</p>
                        <div className="flex flex-wrap gap-1">
                            {Object.keys(source.allowed_methods || {}).map((method) => (
                                <span
                                    key={method}
                                    className="px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs"
                                >
                                    {method.replaceAll('_', ' ')}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/* -------------------- MAIN COMPONENT -------------------- */

const Dynacrop = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState({ results: [], pagination: {} });
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

    // Sidebar state
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [selectedPolygon, setSelectedPolygon] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const fetchPolygons = async (page, size, search = '', ordering = '') => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                page: page,
                size: size,
                search: search,
                ordering: ordering
            };
            const result = await getPolygons(params);

            setData(result || { results: [], pagination: {} });
        } catch (err) {
            console.error(err);
            setError('Failed to fetch data.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let ordering = '';
        if (sortConfig.key && sortConfig.direction) {
            let sortKey = sortConfig.key;
            if (sortKey === 'planStart') sortKey = 'plan.start';
            if (sortKey === 'planEnd') sortKey = 'plan.end';
            ordering = sortConfig.direction === 'desc' ? `-${sortKey}` : sortKey;
        }
        fetchPolygons(currentPage, rowsPerPage, searchQuery, ordering);
    }, [currentPage, rowsPerPage, searchQuery, sortConfig]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= (data.pagination.pages || 1)) {
            setCurrentPage(newPage);
        }
    };

    const handleRowsPerPageChange = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const handleSortChange = ({ key, filter }) => {
        const direction = filter === 'none' ? null : filter;
        setSortConfig({ key, direction });
    };

    const handleCopy = (text, label = "ID") => {
        navigator.clipboard.writeText(text);
        toast.success(`${label} copied to clipboard!`);
    };

    const handleRowClick = async (row) => {
        setDetailLoading(true);
        setSidebarOpen(true);
        try {
            const detail = await getPolygon(row.id);
            setSelectedPolygon(detail);
        } catch (err) {
            console.error(err);
            toast.error('Failed to load polygon details');
        } finally {
            setDetailLoading(false);
        }
    };

    const closeSidebar = () => {
        setSidebarOpen(false);
        setSelectedPolygon(null);
    };

    const handleDelete = async () => {
        if (!selectedPolygon) return;

        const confirmDelete = window.confirm(`Are you sure you want to delete "${selectedPolygon.label || 'this polygon'}"? This action cannot be undone.`);
        if (!confirmDelete) return;

        try {
            await deletePolygon(selectedPolygon.id);
            toast.success('Polygon deleted successfully!');
            closeSidebar();
            let ordering = '';
            if (sortConfig.key && sortConfig.direction) {
                let sortKey = sortConfig.key;
                if (sortKey === 'planStart') sortKey = 'plan.start';
                if (sortKey === 'planEnd') sortKey = 'plan.end';
                ordering = sortConfig.direction === 'desc' ? `-${sortKey}` : sortKey;
            }
            fetchPolygons(currentPage, rowsPerPage, searchQuery, ordering);
        } catch (err) {
            console.error(err);
            toast.error('Failed to delete polygon');
        }
    };

    const parseWKTPolygon = (wkt) => {
        if (!wkt) return [];
        try {
            const match = wkt.match(/POLYGON \(\((.+)\)\)/);
            if (!match) return [];
            const coordsString = match[1];
            const coords = coordsString.split(',').map(coord => {
                const [lng, lat] = coord.trim().split(' ').map(Number);
                return { lat, lng };
            });
            return coords;
        } catch (e) {
            return [];
        }
    };

    const getPolygonCenter = (coords) => {
        if (!coords || coords.length === 0) return [22.7196, 75.8577];
        const sum = coords.reduce(
            (acc, coord) => ({
                lat: acc.lat + coord.lat,
                lng: acc.lng + coord.lng,
            }),
            { lat: 0, lng: 0 }
        );
        return [sum.lat / coords.length, sum.lng / coords.length];
    };

    const formatArea = (area) => {
        if (!area) return '-';
        const hectares = area / 10000;
        return `${hectares.toFixed(2)} ha`;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'bg-green-100 text-green-700';
            case 'pending': return 'bg-yellow-100 text-yellow-700';
            case 'failed': return 'bg-red-100 text-red-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    const polygonCoords = selectedPolygon ? parseWKTPolygon(selectedPolygon.geometry) : [];
    const mapCenter = getPolygonCenter(polygonCoords);

    // Calculate total observations
    const totalObservations = useMemo(() => {
        if (!selectedPolygon?.sources) return 0;
        return selectedPolygon.sources.reduce((sum, src) => sum + (src.valid_observations?.length || 0), 0);
    }, [selectedPolygon]);

    const isExpiringSoon = (dateString) => {
        if (!dateString) return false;
        const date = new Date(dateString);
        const today = new Date();
        const futureDate = new Date();
        futureDate.setDate(today.getDate() + 30);

        // Normalize times to midnight
        date.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        futureDate.setHours(0, 0, 0, 0);

        return date >= today && date <= futureDate;
    };

    const rows = (data.results || []).map((item, index) => {
        return {
            id: item.id || `row-${index}`,
            displayId: (
                <div className="flex items-center justify-center">
                    <FaCopy
                        className="text-gray-400 hover:text-primary cursor-pointer text-sm"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(item.id);
                        }}
                        title={`Copy ID: ${item.id}`}
                    />
                </div>
            ),
            label: item.label || '-',
            area: formatArea(item.area),
            planName: item.plan?.name || '-',
            planStart: item.plan?.start || '-',
            planEnd: (
                <span className={isExpiringSoon(item.plan?.end) ? "text-red-600 font-bold" : ""}>
                    {item.plan?.end || '-'}
                </span>
            ),
            created: item.created ? formatDate(item.created) : '-'
        };
    });

    const headers = [
        { key: 'displayId', label: 'ID', width: '50px', needFilter: false },
        { key: 'label', label: 'Label', width: '180px', needFilter: true },
        { key: 'area', label: 'Area', width: '100px', needFilter: false },
        { key: 'planName', label: 'Plan', width: '120px', needFilter: false },
        { key: 'planStart', label: 'Start Date', width: '120px', needFilter: true },
        { key: 'planEnd', label: 'End Date', width: '120px', needFilter: true },
        { key: 'created', label: 'Created At', width: '120px', needFilter: true },
    ];

    const totalPages = data.pagination.pages || 1;

    return (
        <div className="relative">
            <Breadcrumbs items={[
                { name: 'Home', link: '/dashboard' },
                { name: 'Satellite', link: '/satellite/list' },
                { name: 'Dynacrop' },
            ]} />

            <div className="py-3">
                <div className="flex items-center justify-between mb-4">
                    <StyledHeadingText text="Dynacrop Polygons" />
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    {/* Controls Row */}
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                        <div className="relative flex-1 min-w-[200px] max-w-md">
                            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search polygons..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                            />
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-500">Per Page:</span>
                            <select
                                value={rowsPerPage}
                                onChange={handleRowsPerPageChange}
                                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                            >
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex justify-center py-12">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                        </div>
                    ) : error ? (
                        <div className="text-center py-12 text-red-500">{error}</div>
                    ) : (
                        <>
                            <Table
                                rows={rows}
                                headers={headers}
                                onSortChange={handleSortChange}
                                onRowClick={handleRowClick}
                            />
                            {/* Pagination */}
                            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                                <p className="text-sm text-gray-500">
                                    Total: <span className="font-medium text-gray-700">{data.pagination.total || 0}</span> polygons
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage <= 1}
                                        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150"
                                    >
                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                        <span className="hidden sm:inline text-sm font-medium">Previous</span>
                                    </button>

                                    <div className="px-3 py-2 bg-primary text-white rounded-lg text-sm font-medium min-w-[2.5rem] text-center shadow-sm">
                                        {currentPage}
                                    </div>

                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage >= totalPages}
                                        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150"
                                    >
                                        <span className="hidden sm:inline text-sm font-medium">Next</span>
                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Sidebar Drawer */}
            <div
                className={`fixed top-0 right-0 h-full bg-white shadow-2xl z-50 transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'
                    }`}
                style={{ width: '550px', maxWidth: '95vw' }}
            >
                {/* Sidebar Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-blue-50">
                    <h2 className="text-lg font-bold text-gray-800">
                        {selectedPolygon?.label || 'Polygon Details'}
                    </h2>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => navigate(`/satellite/dynacrop/${selectedPolygon?.id}`)}
                            disabled={!selectedPolygon}
                            className="p-2 hover:bg-blue-100 rounded-full transition-colors disabled:opacity-50"
                            title="View Full Details"
                        >
                            <FaExternalLinkAlt className="text-blue-500 text-sm" />
                        </button>
                        <button
                            onClick={handleDelete}
                            disabled={!selectedPolygon || detailLoading}
                            className="p-2 hover:bg-red-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Delete Polygon"
                        >
                            <FaTrash className="text-red-500" />
                        </button>
                        <button
                            onClick={closeSidebar}
                            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                        >
                            <FaTimes className="text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* Sidebar Content */}
                <div className="overflow-y-auto h-[calc(100%-60px)] p-4">
                    {detailLoading ? (
                        <div className="flex flex-col justify-center items-center h-48">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                            <p className="mt-3 text-sm text-gray-500">Loading details...</p>
                        </div>
                    ) : selectedPolygon ? (
                        <div className="space-y-4">
                            {/* Map Section */}
                            {polygonCoords.length > 0 && (
                                <div className="rounded-lg overflow-hidden border border-gray-200">
                                    <MapComponent
                                        mapLocation={mapCenter}
                                        zoomLevel={15}
                                        height="220px"
                                        areasList={[{
                                            area: polygonCoords,
                                            title: selectedPolygon.label || 'Polygon'
                                        }]}
                                        boundaryColors={['#2FB95D']}
                                    />
                                </div>
                            )}

                            {/* Quick Stats */}
                            <div className="grid grid-cols-4 gap-2">
                                <div className="bg-green-50 rounded-lg p-3 text-center">
                                    <p className="text-xl font-bold text-green-600">{formatArea(selectedPolygon.area)}</p>
                                    <p className="text-xs text-green-600">Area</p>
                                </div>
                                <div className="bg-blue-50 rounded-lg p-3 text-center">
                                    <p className="text-xl font-bold text-blue-600">{selectedPolygon.sources?.length || 0}</p>
                                    <p className="text-xs text-blue-600">Sources</p>
                                </div>
                                <div className="bg-purple-50 rounded-lg p-3 text-center">
                                    <p className="text-xl font-bold text-purple-600">{totalObservations.toLocaleString()}</p>
                                    <p className="text-xs text-purple-600">Total Obs</p>
                                </div>
                                <div className="bg-amber-50 rounded-lg p-3 text-center">
                                    <p className="text-xl font-bold text-amber-600">{selectedPolygon.plan?.name || '-'}</p>
                                    <p className="text-xs text-amber-600">Plan</p>
                                </div>
                            </div>

                            {/* Basic Info */}
                            <div className="bg-gray-50 rounded-lg p-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase tracking-wide">ID</p>
                                        <div className="flex items-center gap-1 mt-1">
                                            <p className="text-sm font-mono text-gray-700 truncate" title={selectedPolygon.id}>
                                                {selectedPolygon.id?.slice(0, 8)}...
                                            </p>
                                            <button
                                                onClick={() => handleCopy(selectedPolygon.id)}
                                                className="p-1 hover:text-primary transition-colors text-gray-400"
                                                title="Copy Full ID"
                                            >
                                                <FaCopy className="text-xs" />
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase tracking-wide">Created</p>
                                        <p className="text-sm font-semibold text-gray-800 mt-1">
                                            {selectedPolygon.created ? formatDate(selectedPolygon.created) : '-'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Plan Info */}
                            {selectedPolygon.plan && (
                                <div className="bg-white border border-gray-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <FaCalendarAlt className="text-primary text-sm" />
                                        <h3 className="text-sm font-semibold text-gray-800">Plan</h3>
                                    </div>
                                    <div className="grid grid-cols-3 gap-3 text-sm">
                                        <div>
                                            <p className="text-xs text-gray-500">Name</p>
                                            <p className="font-medium text-gray-800">{selectedPolygon.plan.name || '-'}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">Start</p>
                                            <p className="font-medium text-gray-800">{selectedPolygon.plan.start || '-'}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">End</p>
                                            <p className="font-medium text-gray-800">{selectedPolygon.plan.end || '-'}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Geometry */}
                            {selectedPolygon.geometry && (
                                <div className="bg-white border border-gray-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <FaMapMarkedAlt className="text-primary text-sm" />
                                            <h3 className="text-sm font-semibold text-gray-800">Geometry</h3>
                                        </div>
                                        <button
                                            onClick={() => handleCopy(selectedPolygon.geometry, "Geometry")}
                                            className="flex items-center gap-1 px-2 py-1 text-xs text-primary bg-primary/10 rounded hover:bg-primary/20"
                                        >
                                            <FaCopy className="text-xs" /> Copy
                                        </button>
                                    </div>
                                    <div className="bg-gray-50 rounded p-2 overflow-x-auto">
                                        <code className="text-xs text-gray-600 break-all">
                                            {selectedPolygon.geometry.slice(0, 80)}...
                                        </code>
                                    </div>
                                </div>
                            )}

                            {/* Sources with Charts */}
                            {selectedPolygon.sources && selectedPolygon.sources.length > 0 && (
                                <div>
                                    <div className="flex items-center gap-2 mb-3">
                                        <FaSatellite className="text-primary" />
                                        <h3 className="font-semibold text-gray-800">Satellite Sources</h3>
                                    </div>
                                    <div className="space-y-3">
                                        {selectedPolygon.sources.map((source, idx) => (
                                            <SourceAccordion key={idx} source={source} isFirst={idx === 0} />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* View Full Details Button */}
                            <button
                                onClick={() => navigate(`/satellite/dynacrop/${selectedPolygon.id}`)}
                                className="w-full py-3 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-lg font-medium hover:from-green-600 hover:to-blue-600 transition-all shadow-sm flex items-center justify-center gap-2"
                            >
                                <FaExternalLinkAlt className="text-sm" />
                                View Full Analysis Page
                            </button>
                        </div>
                    ) : null}
                </div>
            </div>

            {/* Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/30 z-40"
                    onClick={closeSidebar}
                />
            )}
        </div>
    );
};

export default Dynacrop;
